import * as React from "react";
import { useCallback, useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import chroma from "chroma-js";
import i18n from "i18n";
import { Alert } from "components/bootstrap";
import { AccessibleCard } from "components/common";
import GenericPlot from "views/components/visualizations/GenericPlot";
import { defaultChartColors } from "views/components/visualizations/Colors";
import LegendsWithPercent from "views/components/visualizations/legends/LegendsWithPercent";
const CHART_HEIGHT = 240;
const EMPTY_TEXT = "token_usage_pie_chart.empty";
const SELECTED_PULL = 0.06;
const NO_SELECTION = [];
const ChartCard = styled(AccessibleCard)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.md};
    height: 100%;
  `
);
const ChartTitle = styled.h3`
  margin: 0;
`;
const ChartContainer = styled.div`
  height: ${CHART_HEIGHT}px;
  min-height: 0;
`;
const colorFor = (index) => defaultChartColors[index % defaultChartColors.length];
const DIMMED_MIX_LIGHT = 0.4;
const DIMMED_MIX_DARK = 0.85;
const dimmedMixFor = (background) => {
  const luminance = chroma(background).luminance();
  return DIMMED_MIX_DARK + luminance * (DIMMED_MIX_LIGHT - DIMMED_MIX_DARK);
};
const sliceColor = (index, value, selected, background) => selected.length > 0 && !selected.includes(value) ? chroma.mix(colorFor(index), background, dimmedMixFor(background)).css() : colorFor(index);
const pieChartData = (slices, colors, selected) => [
  {
    type: "pie",
    labels: slices.map(({ label }) => label),
    values: slices.map(({ totalTokens }) => totalTokens),
    // Carry each slice's dimension value on the trace so a click can resolve it without a separate lookup.
    customdata: slices.map(({ value }) => value),
    marker: { colors },
    // Pull the selected wedges out so the active filter reads at a glance.
    pull: slices.map(({ value }) => selected.includes(value) ? SELECTED_PULL : 0),
    hole: 0.35,
    sort: false,
    textinfo: "percent",
    hovertemplate: "%{label}<br>%{value:,} tokens<br>%{percent}<extra></extra>",
    automargin: true
  }
];
const chartLayout = {
  height: CHART_HEIGHT,
  margin: {
    t: 0,
    r: 0,
    b: 0,
    l: 0
  },
  showlegend: false
};
const TokenUsagePieChart = ({ title, slices, selected = NO_SELECTION, onSelect = void 0 }) => {
  const usage = useMemo(() => slices.filter(({ totalTokens: totalTokens2 }) => totalTokens2 > 0), [slices]);
  const { colors: themeColors } = useTheme();
  const background = themeColors.global.contentBackground;
  const sliceColors = useMemo(
    () => usage.map(({ value }, index) => sliceColor(index, value, selected, background)),
    [usage, selected, background]
  );
  const chartData = useMemo(() => pieChartData(usage, sliceColors, selected), [usage, sliceColors, selected]);
  const totalTokens = usage.reduce((sum, { totalTokens: tokens }) => sum + tokens, 0);
  const legends = usage.map(({ label, totalTokens: tokens }, index) => ({
    color: sliceColors[index],
    label,
    value: label,
    numberValue: tokens,
    percentageValue: tokens / totalTokens
  }));
  const handleSliceClick = useCallback(
    (_marker, event) => {
      var _a, _b, _c, _d;
      const pointNumber = (_b = (_a = event == null ? void 0 : event.points) == null ? void 0 : _a[0]) == null ? void 0 : _b.pointNumber;
      const value = typeof pointNumber === "number" ? (_d = (_c = chartData[0]) == null ? void 0 : _c.customdata) == null ? void 0 : _d[pointNumber] : void 0;
      if (onSelect && typeof value === "string") {
        onSelect(value);
      }
    },
    [chartData, onSelect]
  );
  return /* @__PURE__ */ React.createElement(ChartCard, { title }, /* @__PURE__ */ React.createElement(ChartTitle, null, title), totalTokens === 0 ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, i18n.t(EMPTY_TEXT, { ns: "enterprise_ai" })) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ChartContainer, null, /* @__PURE__ */ React.createElement(GenericPlot, { chartData, layout: chartLayout, onClickMarker: handleSliceClick })), /* @__PURE__ */ React.createElement(LegendsWithPercent, { legends })));
};
export default TokenUsagePieChart;
